import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon'; // Updated import path

// Can use this in calculations (ex: to shift HorizontalControls when left sidepanel open)
export const FLOWCHART_SIDE_PANEL_WIDTH = 40;

export const FLOWCHART_SIDEPANEL_Z_INDEX = 10;

interface IconConfig {
  name: BenchlingIconName;
  onClick: () => void;
  tooltip?: string;
  tooltipPlacement?: FloatingPlacement;
}

export interface FlowchartSidebarProps {
  icons: Array<IconConfig>;
  selectedIconIndex: number | null;
  setSelectedIconIndex: (index: number | null) => void;
}

/**
 * A sidebar to toggle between different flowchart-related settings and content.
 *
 * @example
 * function FlowchartSidebarExample() {
 *   const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);
 *   const [isSidepanelOpen, setIsSidepanelOpen] = useState<boolean>(false);

 *   const iconsToDisplay = [
 *     {
 *       name: 'wand-magic-sparkles',
 *       onClick: () => {
 *         setIsSidepanelOpen(true);
 *       },
 *       tooltip: 'Add Nodes',
 *     },
 *   ];
 *
 *   return (
 *     <div>
 *       <FlowchartSidebar
 *         icons={iconsToDisplay}
 *         selectedIconIndex={selectedIconIndex}
 *         setSelectedIconIndex={setSelectedIconIndex}
 *       />
 *       <FlowchartContextPanel
 *         isOpen={isSidepanelOpen}
 *         onClose={() => {
 *           setIsSidepanelOpen(false);
 *           setSelectedIconIndex(null);
 *         }}
 *         sidepanelName="Add Nodes"
 *         sidepanelIcon="wand-magic-sparkles"
 *         panelSide={PanelSide.LEFT}
 *       >
 *         Add Nodes content
 *       </FlowchartContextPanel>
 *     </div>
 *   );
 * }
 */
function FlowchartSidebar({
  icons,
  selectedIconIndex,
  setSelectedIconIndex,
}: FlowchartSidebarProps): JSX.Element {
  const prevSelectedIconIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      selectedIconIndex != null &&
      selectedIconIndex !== prevSelectedIconIndexRef.current &&
      icons[selectedIconIndex]
    ) {
      icons[selectedIconIndex].onClick();
    }
    prevSelectedIconIndexRef.current = selectedIconIndex;
  }, [selectedIconIndex, icons]);

  return (
    <div
      className={css(styles.sidebarContainer)}
      {...getTestProps({component: 'FlowchartSidebar', element: 'root'})}
    >
      {icons.map((iconConfig, index) => (
        <Tooltip
          key={index}
          placement={iconConfig.tooltipPlacement ?? FloatingPlacement.TOP}
          text={iconConfig.tooltip}
        >
          <div key={index} className={css(styles.iconContainer)}>
            <div
              className={css(styles.iconWrapper, selectedIconIndex === index && styles.selectedIconWrapper)}
              onClick={() => {
                if (selectedIconIndex === index) {
                  setSelectedIconIndex(null);
                } else {
                  setSelectedIconIndex(index);
                }
              }}
              key={index}
              {...getTestProps({
                component: 'FlowchartSidebar',
                element: 'interactiveIcon',
                key: iconConfig.name,
              })}
            >
              <BenchlingIcon icon={iconConfig.name} className={css(styles.icon)} />
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

export default FlowchartSidebar;

const styles = StyleSheet.create({
  sidebarContainer: {
    width: `${FLOWCHART_SIDE_PANEL_WIDTH}px`,
    height: '100%',
    backgroundColor: Color.WHITE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRight: `1px solid ${Color.GRAY_200}`,
  },
  icon: {
    width: '16px',
    height: '16px',
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px',
  },
  iconWrapper: {
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    borderRadius: '4px',
    color: Color.GRAY_600,
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
    ':active': {
      backgroundColor: Color.GRAY_200,
    },
  },
  selectedIconWrapper: {
    backgroundColor: Color.BLUE_100,
    color: Color.BLUE_450,
    ':hover': {
      backgroundColor: Color.BLUE_100,
    },
    ':active': {
      backgroundColor: Color.BLUE_200,
    },
  },
});
